import React, { memo, useCallback, useEffect, useState } from 'react';

import { Container, Section, SectionTitle, StyledHeader } from './styles';
import { useContextStore } from '../../stores/contextStore';
import Loader from '../../presentation/components/Loader/Loader';
import { CropConnectLanguages } from '../../services/cropConnectApiClient';
import { getLanguagesByUserLanguage } from '../../i18n/locales/languages';
import UserInfo from '../../presentation/features/settings/UserInfo/UserInfo';
import AccountSettings from '../../presentation/features/settings/AccountSettings/AccountSettings';
import { useI18n } from '../../hooks/useI18n';
import { TranslationKeys } from '../../i18n/locales/defaultResources';
import { useProfileStore } from '../../stores/profileStore';

const UserSettings: React.FC = () => {
  const { user, contextUpdating, selectedUserLanguage, updateUserDisplayName } =
    useContextStore();
  const { changeUserLanguage, userLanguage } = useProfileStore();

  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('********');
  const [language, setLocalLanguage] = useState<CropConnectLanguages>(
    selectedUserLanguage as CropConnectLanguages
  );
  const [languages, setLanguages] = useState<
    { value: string; label: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();

  useEffect(() => {
    setLocalLanguage(userLanguage);
  }, [userLanguage]);

  useEffect(() => {
    if (user) {
      setUserName(user.displayName || '');
      setEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    const fetchLanguages = async () => {
      const langOptions = getLanguagesByUserLanguage(
        selectedUserLanguage as CropConnectLanguages
      );
      const langArray = Object.entries(langOptions)
        .filter((l) => l[0] != CropConnectLanguages.None)
        .map(([code, name]) => ({
          value: code,
          label: name,
        }));
      setLanguages(langArray);
      setLoading(false);
    };

    fetchLanguages();
  }, [selectedUserLanguage]);

  const handleLocalLanguageChange = useCallback(
    async (newLanguage: string) => {
      setLocalLanguage(newLanguage as CropConnectLanguages);
      setLoading(true);
      await changeUserLanguage(newLanguage as CropConnectLanguages);
      setLoading(false);
    },
    [changeUserLanguage]
  );

  const handleSaveLanguage = useCallback(async () => {
    setLoading(true);
    await changeUserLanguage(language);
    setLoading(false);
  }, [language, changeUserLanguage]);

  const handleOnDisplayNameSave = (newUserName: string) => {
    updateUserDisplayName(newUserName)
      .then(() => {})
      .catch((e) => {
        console.error('Error updating user display name', e);
      });
  };

  if (contextUpdating || loading) {
    return <Loader isFullScreen={true} />;
  }

  return (
    <Container>
      <Section>
        <StyledHeader>
          {t(TranslationKeys.userSettings_pageHeader)}
        </StyledHeader>
        <SectionTitle>
          {t(TranslationKeys.userSettings_subtitle_contactInformation)}
        </SectionTitle>
        <UserInfo
          userName={userName}
          setUserName={setUserName}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          handleOnDisplayNameSave={handleOnDisplayNameSave}
        />
      </Section>
      <Section>
        <SectionTitle>
          {t(TranslationKeys.userSettings_subtitle_accountSettings)}
        </SectionTitle>
        <AccountSettings
          label={t(TranslationKeys.userSettings_label_language)}
          language={language}
          languages={languages}
          handleLanguageChange={handleLocalLanguageChange}
          handleSaveLanguage={handleSaveLanguage}
        />
      </Section>
    </Container>
  );
};

export default memo(UserSettings);
